import { toast } from "react-toastify";
import * as Yup from "yup";
import { useState } from "react";
import Cookies from "js-cookie";
import axios from "axios";
import { TailSpin, ThreeDots } from "react-loader-spinner";
import validations from "../../config/validation";
import Input from "../../components/Input";
import { API_CREATE_ACCESS_TYPE } from "../../config/Endpoints";

const CreateAccessTypeModal = ({ accessTypeList, setAccessTypes }) => {
  const [isLoading, setIsLoading] = useState(false);
  const formDataInitialState = {
    accessName: "",
    description: "",
    isRole: "",
  };
  const [formData, setFormData] = useState(formDataInitialState);
  const [errors, setErrors] = useState({});
  const [accessTypeAlreadyExists, setAccessTypeAlreadyExists] = useState(false);
  const accessToken = Cookies.get("accessToken");

  const validationSchema = Yup.object().shape({
    accessName:
      validations.ONLY_SPECIAL_AND_NUMBERS_NOT_ALLOWED_VALIDATION.required(
        "Access Type Name is required."
      ),
    description: validations.COMMENT_VALIDATION.min(
      5,
      "Description must be at least 5 characters long."
    ).required("Description is required."),
    isRole: Yup.string().required("Please select if it is a Role or not."),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Reset errors
      setErrors({});

      // Validate form data
      await validationSchema.validate(formData, { abortEarly: false });

      // API call
      const res = await axios.post(
        API_CREATE_ACCESS_TYPE,
        {
          accessName: formData.accessName,
          description: formData.description,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          params: { role: formData.isRole },
        }
      );

      toast.success(res.data?.message || "Access type created successfully!", {
        position: toast.POSITION.TOP_RIGHT,
      });

      setAccessTypes((prev) => [
        ...prev,
        {
          name: formData.accessName,
          description: formData.description,
          isRole: Number(formData.isRole),
        },
      ]);

      document.getElementsByClassName("cancel-modal")[0].click();
      handleReset();
    } catch (err) {
      // Check if it's a Yup validation error
      if (err.name === "ValidationError") {
        const validationErrors = {};
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
      } else {
        // API or other errors
        toast.error(
          err.response?.data?.message ||
            "Something went wrong. Please try again!",
          { position: toast.POSITION.TOP_RIGHT }
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === "accessName") {
      // Remove leading spaces
      newValue = newValue.replace(/^\s+/, "");
      // Replace multiple spaces inside with a single space
      newValue = newValue.replace(/\s{2,}/g, " ");
    }

    setFormData({ ...formData, [name]: newValue });

    const inputValue = newValue.toLowerCase();

    if (name === "accessName" && Array.isArray(accessTypeList)) {
      const exists = accessTypeList.some(
        (obj) => obj.accessName?.toLowerCase() === inputValue.trim()
      );
      setAccessTypeAlreadyExists(exists);
      setErrors((prev) => ({
        ...prev,
        accessName: exists ? "Access type already exists." : "",
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleReset = () => {
    setFormData(formDataInitialState);
    setErrors({});
  };

  return (
    <form onSubmit={handleSubmit}>
      {isLoading && (
        <div className="overlay">
          <div className="loader-container">
            <TailSpin
              height="100"
              width="100"
              color="blue"
              ariaLabel="loading"
            />
          </div>
        </div>
      )}

      <div className="modal-header">
        <h5 className="modal-title text-center flex">Create Access Type</h5>
        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="modal"
          aria-label="Close"
          onClick={handleReset}
        ></button>
      </div>

      <div className="modal-body">
        <div className="px-2 py-4">
          <div className="d-grid inputs gap-4">
            {/* Access Name */}
            <div className="input-group">
              <div className="row">
                <div className="col-md-2"></div>
                <div className="col-md-4 pt-1">
                  <label htmlFor="accessName" className="has-asterisk">
                    Access Type Name
                  </label>
                </div>
                <div className="col-md-4">
                  <Input
                    type="text"
                    name="accessName"
                    className="border"
                    onChange={handleInputChange}
                    required
                    value={formData.accessName}
                    disabled={isLoading}
                    autoComplete="off"
                  />
                  {errors.accessName && (
                    <small className="text-danger">{errors.accessName}</small>
                  )}
                </div>
                <div className="col-md-2"></div>
              </div>
            </div>

            {/* Is Role */}
            <div className="input-group">
              <div className="row">
                <div className="col-md-2"></div>
                <div className="col-md-4 pt-1">
                  <label htmlFor="isRole" className="has-asterisk">
                    Extended Roles
                  </label>
                </div>
                <div className="col-md-4">
                  <select
                    id="isRole"
                    name="isRole"
                    className=" border"
                    value={formData.isRole}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select</option>
                    <option value="1">Yes</option>
                    <option value="0">No</option>
                  </select>
                  {errors.isRole && (
                    <small className="text-danger">{errors.isRole}</small>
                  )}
                </div>
                <div className="col-md-2"></div>
              </div>
            </div>

            {/* Description */}
            <div className="input-group">
              <div className="row">
                <div className="col-md-2"></div>
                <div className="col-md-4 pt-1">
                  <label htmlFor="description" className="has-asterisk">
                    Description
                  </label>
                </div>
                <div className="col-md-4">
                  <textarea
                    id="description"
                    rows={3}
                    className="border"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                  ></textarea>
                  {errors.description && (
                    <small className="text-danger">{errors.description}</small>
                  )}
                </div>
                <div className="col-md-2"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="justify-content-center modal-footer">
        <button
          className={`theme-button ${
            isLoading || accessTypeAlreadyExists ? "disabled" : ""
          }`}
          type="submit"
          disabled={accessTypeAlreadyExists}
        >
          {isLoading ? (
            <ThreeDots height="22" width="60" color="white" visible={true} />
          ) : (
            "Submit"
          )}
        </button>
        <button
          type="button"
          className="theme-button bg-grey mx-3 w-35 cancel-modal"
          data-bs-dismiss="modal"
          onClick={handleReset}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default CreateAccessTypeModal;
